import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';

import CloseIcon from '@mui/icons-material/Close';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import VerifyIcon from '@mui/icons-material/VerifiedUserOutlined';
import SmsIcon from '@mui/icons-material/SmsOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import EnableIcon from '@mui/icons-material/Beenhere';
import DisableIcon from '@mui/icons-material/NotInterested';
import MobileOff from '@mui/icons-material/MobileOff';
import LoginIcon from '@mui/icons-material/Login';

import Table from '../shared/table';
import UserNameCell from '../shared/table/UserNameCell';
import CompanyCell from '../shared/table/CompanyCell';

import { userRoles } from '../../constants';

import './users.scss';

const getStatusChipSx = (theme, isActive) => {
  if (isActive) {
    return {
      dot: '#17B26A',
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(23,178,106,0.16)' : '#ECFDF3',
      borderColor: theme.palette.mode === 'dark' ? 'rgba(23,178,106,0.28)' : '#ABEFC6',
      color: theme.palette.mode === 'dark' ? '#75e0a7' : '#067647',
    };
  }

  return {
    dot: 'var(--color-text-muted)',
    backgroundColor: theme.palette.mode === 'dark' ? 'var(--color-hover-surface)' : '#FAFAFA',
    borderColor: 'var(--color-border)',
    color: theme.palette.text.secondary,
  };
};

const hasActiveUserFilters = (filter = {}) => (
  Object.keys(filter).some((key) => (
    Array.isArray(filter[key])
      ? filter[key].length > 0
      : !!(filter[key]?.from || filter[key]?.to)
  ))
);

const getLastSignInFilterLabel = (dateFilter) => (
  `${dateFilter?.from ? moment(dateFilter.from).format('DD/MM/YY') : 'Any'} - ${dateFilter?.to ? moment(dateFilter.to).format('DD/MM/YY') : 'Any'}`
);

const UsersTable = (props) => {
  const {
    users = [],
    distributors = [],
    onVerifyClick = () => {
    },
    onSMSClick = () => {
    },
    onEditClick = () => {
    },
    onDisableClick = () => {
    },
    onDisable2FAClick = () => {
    },
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalRows,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    companies,
    userRole,
    currentUserId,
    isGhostMode = false,
    canUseGhostMode = false,
    filter,
    onRemoveFilterItem = () => {
    },
    onGhostModeClick = () => {
    },
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const formatFilterValue = (key, value) => {
    if (key === 'distributors') {
      const distributor = distributors?.find((d) => d.id === value);
      return distributor?.name || '';
    }
    if (key === 'companies') {
      const company = companies?.find((c) => c.id === value);
      return company?.name || '';
    }
    if (key === 'roles') {
      return userRoles[value]?.label || '';
    }
    if (key === 'statuses') {
      return value?.value === 'disabled' ? 'Disabled' : 'Active';
    }

    return value;
  };

  const columns = useMemo(() => {
    if ([userRoles.COUNTRY_ADMIN.name, userRoles.DISTRIBUTOR_ADMIN.name].includes(userRole)) {
      return [
        {
          title: 'Full Name',
          field: 'fullName',
          type: 'custom',
          sort: true,
          formatter: (value, row) => <UserNameCell user={row} />,

        },
        {
          title: 'Role',
          field: 'role',
          type: 'custom',
          sort: true,
          filter: true,
          formatter: (value, row) => (
            value === userRoles.ENGINEER.name ?
              `${userRoles.ENGINEER.label} ${!!row.engineerLevel ? `(Level ${row.engineerLevel})` : ''}` : userRoles[value]?.label || ''
          ),
        },
        {
          title: 'Countries',
          field: 'countries',
          type: 'custom',
          filter: true,
          formatter: (value) => value?.map((country) => (
            <Chip
              key={country}
              size="small"
              label={country}
              variant="outlined"
              sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', mr: 1 }}
            />
          )),
        },
        { title: 'Distributors', field: 'distributorNames', type: 'default', filter: true },
        {
          title: 'Last Login',
          field: 'lastSignInTime',
          type: 'custom',
          sort: true,
          formatter: (value) => value && moment(value) ? moment(value)?.format('DD-MM-YYYY hh:mm A') : '',
        },
        {
          title: 'Status',
          field: 'disabled',
          type: 'custom',
          filter: false,
          formatter: (value) => (
            <Chip
              size="small"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={(theme) => ({
                      height: '6px',
                      width: '6px',
                      borderRadius: '50%',
                      background: getStatusChipSx(theme, !value).dot,
                      mr: '5px',
                    })}
                  />
                  <Typography fontSize={14}>{!!value ? 'Inactive' : 'Active'}</Typography>
                </Box>
              }
              variant="outlined"
              sx={(theme) => {
                const chipTheme = getStatusChipSx(theme, !value);
                return {
                  fontWeight: 500,
                  backgroundColor: chipTheme.backgroundColor,
                  borderColor: chipTheme.borderColor,
                  color: chipTheme.color,
                };
              }}
            />
          ),
        },
        {
          title: '2FA',
          field: 'multiFactorEnabled',
          type: 'custom',
          filter: false,
          formatter: (value) => (
            <Chip
              size="small"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={(theme) => ({
                      height: '6px',
                      width: '6px',
                      borderRadius: '50%',
                      background: getStatusChipSx(theme, !!value).dot,
                      mr: '5px',
                    })}
                  />
                  <Typography fontSize={14}>{!!value ? 'On' : 'Off'}</Typography>
                </Box>
              }
              variant="outlined"
              sx={(theme) => {
                const chipTheme = getStatusChipSx(theme, !!value);
                return {
                  fontWeight: 500,
                  backgroundColor: chipTheme.backgroundColor,
                  borderColor: chipTheme.borderColor,
                  color: chipTheme.color,
                };
              }}
            />
          ),
        },
        {
          title: '',
          field: 'uid',
          type: 'custom',
          filter: false,
          width: 50,
          formatter: (value, row) => (
            <>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                aria-controls={`${row.uid}_actions`}
                aria-haspopup="true"
                aria-label="actions"
                color="inherit"
                id={`${row.uid}_actions`}
              >
                <MoreIcon color="secondary" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                elevation={1}
                id={`${row.uid}_actions`}
                keepMounted
                onClose={handleMenuClose}
                open={Boolean(anchorEl) && anchorEl.id === `${row.uid}_actions`}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem
                  onClick={() => {
                    onEditClick(row);
                    handleMenuClose();
                  }}
                >
                  <EditIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                  Edit User
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onVerifyClick(value);
                    handleMenuClose();
                  }}
                >
                  <VerifyIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                  Verify Email
                </MenuItem>
                {!!row?.multiFactorEnabled && (
                  <MenuItem
                    onClick={() => {
                      onDisable2FAClick(row);
                      handleMenuClose();
                    }}
                  >
                    <MobileOff fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                    Disable 2FA
                  </MenuItem>
                )}
                {!!row.phone && (
                  <MenuItem
                    onClick={() => {
                      onSMSClick(value);
                      handleMenuClose();
                    }}
                  >
                    <SmsIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                    Send SMS
                  </MenuItem>
                )}
                <Divider />
                <MenuItem
                  onClick={() => {
                    onDisableClick(row?.uid, !row?.disabled);
                    handleMenuClose();
                  }}
                >
                  {!!row.disabled ?
                    <EnableIcon fontSize="small" style={{ marginRight: 8, fill: '#17B26A' }} /> :
                    <DisableIcon fontSize="small" style={{ marginRight: 8, fill: '#F04438' }} />}
                  {!!row.disabled ?
                    <Typography sx={{ color: '#17B26A' }}>Enable User</Typography> :
                    <Typography sx={{ color: '#F04438' }}>Disable User</Typography>
                  }
                </MenuItem>
              </Menu>
            </>
          ),
        },
      ];
    }
    return [
      {
        title: 'Full Name',
        field: 'fullName',
        type: 'custom',
        sort: true,
        formatter: (value, row) => <UserNameCell user={row} />,

      },
      {
        title: 'Role',
        field: 'role',
        type: 'custom',
        sort: true,
        filter: true,
        formatter: (value, row) => (
          value === userRoles.ENGINEER.name ?
            `${userRoles.ENGINEER.label} ${!!row.engineerLevel ? `(Level ${row.engineerLevel})` : ''}` : userRoles[value]?.label || ''
        ),
      },
      {
        title: 'Company',
        field: 'company',
        type: 'custom',
        sort: true,
        formatter: (value) => {
          const company = companies?.find((c) => c.id === value);
          return company ? <CompanyCell company={company} /> : null;
        },
      },
      {
        title: 'Countries',
        field: 'countries',
        type: 'custom',
        filter: true,
        formatter: (value) => value?.map((country) => (
          <Chip
            key={country}
            size="small"
            label={country}
            variant="outlined"
            sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', mr: 1 }}
          />
        )),
      },
      { title: 'Distributors', field: 'distributorNames', type: 'default', filter: true },
      {
        title: 'Last Login',
        field: 'lastSignInTime',
        type: 'custom',
        sort: true,
        formatter: (value) => value && moment(value) ? moment(value)?.format('DD-MM-YYYY hh:mm A') : '',
      },
      {
        title: 'Status',
        field: 'disabled',
        type: 'custom',
        filter: false,
        formatter: (value) => (
          <Chip
            size="small"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={(theme) => ({
                    height: '6px',
                    width: '6px',
                    borderRadius: '50%',
                    background: getStatusChipSx(theme, !value).dot,
                    mr: '5px',
                  })}
                />
                <Typography fontSize={14}>{!!value ? 'Inactive' : 'Active'}</Typography>
              </Box>
            }
            variant="outlined"
            sx={(theme) => {
              const chipTheme = getStatusChipSx(theme, !value);
              return {
                fontWeight: 500,
                backgroundColor: chipTheme.backgroundColor,
                borderColor: chipTheme.borderColor,
                color: chipTheme.color,
              };
            }}
          />
        ),
      },
      {
        title: '2FA',
        field: 'multiFactorEnabled',
        type: 'custom',
        filter: false,
        formatter: (value) => (
          <Chip
            size="small"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={(theme) => ({
                    height: '6px',
                    width: '6px',
                    borderRadius: '50%',
                    background: getStatusChipSx(theme, !!value).dot,
                    mr: '5px',
                  })}
                />
                <Typography fontSize={14}>{!!value ? 'On' : 'Off'}</Typography>
              </Box>
            }
            variant="outlined"
            sx={(theme) => {
              const chipTheme = getStatusChipSx(theme, !!value);
              return {
                fontWeight: 500,
                backgroundColor: chipTheme.backgroundColor,
                borderColor: chipTheme.borderColor,
                color: chipTheme.color,
              };
            }}
          />
        ),
      },
      {
        title: '',
        field: 'uid',
        type: 'custom',
        filter: false,
        width: 50,
        formatter: (value, row) => {
          const canStartGhostMode = (
            canUseGhostMode &&
            !isGhostMode &&
            !row.disabled &&
            row.uid !== currentUserId
          );

          return (
            <>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                aria-controls={`${row.uid}_actions`}
                aria-haspopup="true"
                aria-label="actions"
                color="inherit"
                id={`${row.uid}_actions`}
              >
                <MoreIcon color="secondary" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                elevation={1}
                id={`${row.uid}_actions`}
                keepMounted
                onClose={handleMenuClose}
                open={Boolean(anchorEl) && anchorEl.id === `${row.uid}_actions`}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem
                  onClick={() => {
                    onEditClick(row);
                    handleMenuClose();
                  }}
                >
                  <EditIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                  Edit User
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onVerifyClick(value);
                    handleMenuClose();
                  }}
                >
                  <VerifyIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                  Verify Email
                </MenuItem>
                {!!row?.multiFactorEnabled && (
                  <MenuItem
                    onClick={() => {
                      onDisable2FAClick(row);
                      handleMenuClose();
                    }}
                  >
                    <MobileOff fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                    Disable 2FA
                  </MenuItem>
                )}
                {!!row.phone && (
                  <MenuItem
                    onClick={() => {
                      onSMSClick(value);
                      handleMenuClose();
                    }}
                  >
                    <SmsIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                    Send SMS
                  </MenuItem>
                )}
                {canStartGhostMode && (
                  <MenuItem
                    onClick={() => {
                      onGhostModeClick(row);
                      handleMenuClose();
                    }}
                  >
                    <LoginIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                    Login as User
                  </MenuItem>
                )}
                <Divider />
                <MenuItem
                  onClick={() => {
                    onDisableClick(row?.uid, !row?.disabled);
                    handleMenuClose();
                  }}
                >
                  {!!row.disabled ?
                    <EnableIcon fontSize="small" style={{ marginRight: 8, fill: '#17B26A' }} /> :
                    <DisableIcon fontSize="small" style={{ marginRight: 8, fill: '#F04438' }} />}
                  {!!row.disabled ?
                    <Typography sx={{ color: '#17B26A' }}>Enable User</Typography> :
                    <Typography sx={{ color: '#F04438' }}>Disable User</Typography>
                  }
                </MenuItem>
              </Menu>
            </>
          );
        },
      },
    ];
  }, [
    userRole,
    canUseGhostMode,
    currentUserId,
    isGhostMode,
    anchorEl,
    companies,
    onDisable2FAClick,
    onDisableClick,
    onEditClick,
    onGhostModeClick,
    onSMSClick,
    onVerifyClick,
  ]);

  const getDistributorNames = (distributorIds) => {
    if (!distributorIds || !distributorIds.length) {
      return '';
    }
    return distributorIds?.map((distributorId) => {
      const distributor = distributors?.find((d) => d.id === distributorId);
      return distributor?.name;
    })?.filter(Boolean)?.join(', ');
  };

  const getDistributorCountries = (distributorIds) => {
    if (!distributorIds || !distributorIds.length) {
      return '';
    }
    return distributorIds?.map((distributorId) => {
      const distributor = distributors?.find((d) => d.id === distributorId);
      return distributor?.country;
    })?.filter(Boolean);
  };

  const renderFilterList = () => {
    if (!filter || !hasActiveUserFilters(filter)) {
      return null;
    }

    const renderFilterChip = (key, label, value) => {
      const filterValue = value?.value || value;

      return (
        <div key={filterValue ? `${key}-${filterValue}` : key} className="filter-list-item">
          <div />
          <div className="filter-list-item-value">{label}</div>
          <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
        </div>
      );
    };

    const renderFilterGroup = (key) => {
      if (key === 'lastSignInTime' && filter.lastSignInTime) {
        return (
          <div key={key} className="filter-list-item-container">
            {renderFilterChip(key, getLastSignInFilterLabel(filter.lastSignInTime))}
          </div>
        );
      }

      if (!Array.isArray(filter[key])) {
        return null;
      }

      return (
        <div key={key} className="filter-list-item-container">
          {filter[key].map((value) => renderFilterChip(key, formatFilterValue(key, value), value))}
        </div>
      );
    };

    return (
      <div className="filter-list">
        <FiltersIcon color="secondary" />
        {Object.keys(filter).map(renderFilterGroup)}
      </div>
    );
  };

  return (
    <Fragment>
      {renderFilterList()}
      <Table
        stickyTable
        rows={users?.map((user) => ({
          ...user,
          lastSignInTime: user?.lastSignInTime ? moment(user.lastSignInTime).valueOf() : null,
          fullName: `${user.firstName} ${user.lastName}`,
          distributorNames: getDistributorNames(user.distributors),
          countries: user?.administrationCountries || getDistributorCountries(user.distributors) || [],
        }))}
        columns={columns}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalRows={totalRows}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
    </Fragment>
  );
};

UsersTable.propTypes = {
  users: PropTypes.array,
  distributors: PropTypes.array,
  onVerifyClick: PropTypes.func,
  onSMSClick: PropTypes.func,
  filter: PropTypes.object,
  onRemoveFilterItem: PropTypes.func,
  onGhostModeClick: PropTypes.func,
};

export default UsersTable;
